import type { ReactNode } from 'react'
import { ExternalLink } from '~/components/Typography/ExternalLink'
import Image from '~/components/image/Image'
import { useEnv } from '~/env'
import regions from '~/features/top/data/ikyu/regions.json'
import { DESIGN_SYSTEM_ASSETS_ORIGIN } from '~/lib/constants/constants'
import { css, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { FooterExternalLinks } from './FooterExternalLinks'
import { COPYRIGHT } from './copyright'
import { SCENE } from './data/scene'

/**
 * PCフッター
 *
 * @see {@link https://www.figma.com/design/4RphgTJS4xrTXmtSJS3ANB/RES_UI-Guideline?node-id=6304-5671&m=dev figma}
 */
export const PcFooter = import.meta.env.VITE_MODE === 'ppg' ? PpgPcFooter : IkyuPcFooter

type FooterType = 'full' | 'minimum'

function IkyuPcFooter(props: { type?: FooterType }) {
  const env = useEnv()
  return (
    <FooterBase
      type={props.type}
      aboveTerms={
        <div className={hstack({ justifyContent: 'space-between', w: 'content.base' })}>
          <ul className={hstack({ gap: '0', color: 'gray.700', fontSize: 'sm', py: '2' })}>
            {[
              { href: 'https://www.ikyu.co.jp/', label: '企業情報' },
              { href: 'https://www.ikyu.co.jp/news/', label: 'お知らせ・IR' },
              { href: 'https://www.ikyu.co.jp/recruit/', label: '採用情報' },
            ].map(({ href, label }) => (
              <li
                key={href}
                className={css({
                  borderY: 'medium',
                  borderLeft: 'medium',
                  borderRight: { _last: 'medium' },
                })}
              >
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css({ display: 'block', px: '3', py: '1' })}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
          <ul className={hstack({ gap: '2' })}>
            {[
              { icon: 'facebook-circle', href: 'https://www.facebook.com/IkyuRestaurant' },
              { icon: 'x-circle', href: 'https://x.com/ikyu_restaurant' },
            ].map(({ icon, href }) => (
              <li key={href}>
                <a href={href} target="_blank" rel="noopener noreferrer">
                  <Image
                    src={`${DESIGN_SYSTEM_ASSETS_ORIGIN}/design-system/assets/brand-icons/svg/${icon}.svg`}
                    alt=""
                    width={30}
                    height={30}
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      }
      termLinks={[
        { href: 'https://my.ikyu.com/terms', label: '規約集', external: true },
        {
          href: 'https://www.lycorp.co.jp/ja/company/cybersecurity/',
          label: 'LINEヤフーサイバーセキュリティ基本方針',
          external: true,
        },
        { href: 'https://www.ikyu.co.jp/policy/', label: '個人情報保護方針', external: true },
        { href: 'https://restaurant.ikyu.com/legal.htm', label: '特定商取引法に関する表示' },
        { href: 'https://restaurant.ikyu.com/smap.htm', label: 'サイトマップ' },
        { href: 'https://restaurant.ikyu.com/rsHelp/about.htm', label: 'サイトのご利用' },
      ]}
      belowTermsLinks={[
        { href: env.RESTAURANT_IKYU_ORIGIN, label: 'レストラン予約' },
        { href: `${env.RESTAURANT_IKYU_ORIGIN}/lunch/`, label: 'ランチ' },
        { href: `${env.RESTAURANT_IKYU_ORIGIN}/dinner/`, label: 'ディナー' },
        { href: `${env.RESTAURANT_IKYU_ORIGIN}/spot/`, label: 'おでかけスポット' },
        { href: `${env.RESTAURANT_IKYU_ORIGIN}/area/`, label: 'エリア' },
        { href: `${env.RESTAURANT_IKYU_ORIGIN}/t-genre/`, label: '料理' },
        { href: `${env.RESTAURANT_IKYU_ORIGIN}/t-food/`, label: '食材' },
        { href: `${env.RESTAURANT_IKYU_ORIGIN}/t-scene/`, label: 'シーン' },
      ]}
      regions={<Regions />}
      subSceneLinks={[
        { href: `https://restaurant.ikyu.com/join`, label: '加盟店のお問合せ' },
        {
          href: `${env.RESTAURANT_IKYU_ORIGIN}/rsEtc/valuecommerce/index.htm`,
          label: 'アフィリエイト募集',
        },
      ]}
      aboveRecommendedServiceLinks={[
        { href: 'https://www.ikyu.com/', label: '国内宿泊予約' },
        { href: 'https://www.ikyu.com/global/', label: '海外宿泊予約' },
      ]}
    />
  )
}

function PpgPcFooter(props: { type?: FooterType }) {
  return (
    <FooterBase
      type={props.type}
      termLinks={[
        {
          href: 'https://www.lycorp.co.jp/ja/company/privacypolicy/',
          label: 'プライバシーポリシー',
          external: true,
        },
        { href: 'https://privacy.lycorp.co.jp/ja/', label: 'プライバシーセンター', external: true },
        { href: 'https://www.lycorp.co.jp/ja/company/terms/', label: '規約', external: true },
        { href: '/guidance/consumer-protection', label: '安心・安全への取り組み' },
        { href: 'https://restaurant.ikyu.com/join', label: '加盟店のお問合せ', external: true },
      ]}
      aboveCopyright={
        <p>PayPayグルメはPayPay株式会社の協力のもとLINEヤフー株式会社が運営しているサービスです</p>
      }
    />
  )
}

function FooterBase(props: {
  type?: FooterType
  aboveTerms?: ReactNode
  termLinks: Links
  belowTermsLinks?: Links
  regions?: ReactNode
  subSceneLinks?: Links
  aboveRecommendedServiceLinks?: Links
  aboveCopyright?: ReactNode
}) {
  return (
    <div
      className={css({ borderTop: 'medium', pb: '10' }, !props.aboveTerms && css.raw({ pt: '10' }))}
    >
      <div className={css({ w: 'content.base', spaceY: '10', mx: 'auto' })}>
        <div className={css({ spaceY: '5' })}>
          {props.aboveTerms}
          <LinkList links={props.termLinks} />
          {props.type !== 'minimum' && <LinkList links={props.belowTermsLinks} />}
        </div>
        {props.type !== 'minimum' && (
          <>
            {props.regions}
            <Scenes />
            {<LinkList links={props.subSceneLinks} />}
          </>
        )}
        {<LinkList links={props.aboveRecommendedServiceLinks} />}
        {props.type === 'full' && <FooterExternalLinks />}
        <div
          className={css({ spaceY: '5', color: 'gray.600', fontSize: 'xs', textAlign: 'center' })}
        >
          <p>
            ※掲載の写真はイメージです。※掲載されているすべてのコンテンツの無断での転載、転用、コピー等は禁じます。
          </p>
          {props.aboveCopyright}
          <p>{COPYRIGHT}</p>
        </div>
      </div>
    </div>
  )
}

function Regions() {
  return (
    <ul className={css({ display: 'grid', gridTemplateColumns: '3', w: 'full' })}>
      {adjustedRegions.map((regions, regionsIndex) => (
        <li
          // 静的なJSONに基づくので要素のインデックスをkeyにする。他の箇所も同様
          key={regionsIndex}
          className={css({
            pl: '[56px]',
            borderLeft: 'light',
            spaceY: '10',
            color: 'gray.700',
            _last: { borderRight: 'light' },
          })}
        >
          {regions.map((region, regionIndex) => (
            <dl key={regionIndex} className={css({ spaceY: '5' })}>
              <dt>
                <a href={region.href} className={css({ fontSize: 'md', mb: '5' })}>
                  {region.name}
                </a>
              </dt>

              <dd className={css({ display: 'grid', gridTemplateColumns: '2', gap: '2' })}>
                {region.minors.map((minor, minorIndex) => (
                  <a key={minorIndex} href={minor.href} className={css({ fontSize: 'sm' })}>
                    {minor.name}
                  </a>
                ))}
              </dd>
            </dl>
          ))}
        </li>
      ))}
    </ul>
  )
}

function Scenes() {
  return (
    <div className={css({ spaceY: '5' })}>
      {SCENE.filter((links) => links.visible).map(({ links }, linksIndex) => (
        <LinkList key={linksIndex} links={links.map(({ href, name }) => ({ href, label: name }))} />
      ))}
    </div>
  )
}

interface Region {
  name: string
  href: string
  label: string
  minors: Minor[]
}

interface Minor {
  name: string
  href: string
}

// Regionsを3分割して返す
const adjustedRegions = regions.reduce(
  (arr, region) => {
    const index = { tokyo: 0, kanagawa: 1, aichi: 1 }[region.label] ?? 2
    arr[index].push(region)
    return arr
  },
  [[], [], []] as Region[][]
)

type Links = { href: string; label: string; external?: boolean }[]

const linkListClasses = sva({
  slots: ['list', 'item'],
  base: {
    list: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      fontSize: 'sm',
      color: 'gray.700',
      lineHeight: 'relaxed',
    },
    item: {
      display: 'inline',
      _notLast: {
        _after: {
          content: '"|"',
          mx: '2',
          color: 'gray.300',
        },
      },
    },
  },
})()

function LinkList(props: { links?: Links }) {
  const { list, item } = linkListClasses
  if (!props.links) {
    return null
  }
  return (
    <ul className={list}>
      {props.links.map((link, i) => (
        <li key={i} className={item}>
          {link.external ? (
            <ExternalLink href={link.href} label={link.label} color="gray" />
          ) : (
            <a href={link.href}>{link.label}</a>
          )}
        </li>
      ))}
    </ul>
  )
}
